import { Link } from 'react-router-dom'
import { motion, AnimatePresence, useScroll, useTransform } from 'framer-motion'
import { useState, useEffect } from 'react'
import { Menu, X } from 'lucide-react'
import { Button } from '@/components/ui/button'

const NAV_ITEMS = [
  { label: 'About', href: '/#features' },
  { label: 'Products', href: '/#elements' },
  { label: 'Operations', href: '/#facilities' },
  { label: 'Sustainability', href: '/#sustainability' },
] as const

const STICK_THRESHOLD = 64 // 4rem – scroll distance for header to reach sticky position
const TOP_STICKY_DESKTOP = 12 // 0.75rem
const TOP_STICKY_MOBILE = 16 // 1rem
const TOP_HERO = 64 // 4rem – matches hero layout

export default function Navbar({ light = false, heroLayout = false }: { light?: boolean; heroLayout?: boolean }) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [isLargeViewport, setIsLargeViewport] = useState(false)
  const { scrollY } = useScroll()
  const isCollapsed = true
  const useDarkText = light

  const topSticky = isLargeViewport ? TOP_STICKY_DESKTOP : TOP_STICKY_MOBILE
  const useScrollDrivenTop = heroLayout && isLargeViewport
  const top = useTransform(
    scrollY,
    [0, STICK_THRESHOLD],
    useScrollDrivenTop ? [TOP_HERO, topSticky] : [topSticky, topSticky]
  )

  useEffect(() => {
    const checkViewport = () => setIsLargeViewport(window.innerWidth >= 768)
    checkViewport()
    window.addEventListener('resize', checkViewport)
    return () => window.removeEventListener('resize', checkViewport)
  }, [])

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) setIsMobileMenuOpen(false)
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  useEffect(() => {
    document.body.style.overflow = isMobileMenuOpen ? 'hidden' : ''
    return () => { document.body.style.overflow = '' }
  }, [isMobileMenuOpen])

  return (
    <>
      <motion.header
        className="fixed left-0 right-0 z-50 flex justify-center py-4 px-4"
        style={{ top }}
      >
        <motion.div
          className={`flex items-center justify-between px-6 py-3 transition-all duration-300 ${
            isCollapsed ? 'bg-white shadow-sm border border-slate-100 rounded-lg w-full max-w-5xl' : 'bg-transparent w-full max-w-7xl'
          }`}
          layout
        >
          <Link to="/" className="flex items-center h-12">
            <img
              src="/assets/lynas-logo.png"
              alt="Lynas Rare Earths"
              className="h-8 md:h-10 object-contain object-left"
            />
          </Link>

          <nav
            className={`hidden md:flex items-center gap-8 text-base font-medium transition-colors duration-300 ${
              isCollapsed ? 'text-slate-600' : useDarkText ? 'text-slate-600' : 'text-white/90'
            }`}
          >
            {NAV_ITEMS.map((item) =>
              item.href.startsWith('/') ? (
                <Link
                  key={item.label}
                  to={item.href}
                  className={isCollapsed ? 'text-slate-600 hover:text-accent' : useDarkText ? 'text-slate-600 hover:text-accent' : 'text-white/90 hover:text-white'}
                  style={{ transition: 'color 0.2s' }}
                >
                  {item.label}
                </Link>
              ) : (
                <a
                  key={item.label}
                  href={item.href}
                  className={isCollapsed ? 'text-slate-600 hover:text-accent' : useDarkText ? 'text-slate-600 hover:text-accent' : 'text-white/90 hover:text-white'}
                  style={{ transition: 'color 0.2s' }}
                >
                  {item.label}
                </a>
              )
            )}
            <Link
              to="/investors"
              className={`inline-flex items-center justify-center px-5 py-2 font-semibold rounded-lg transition-colors ${
                isCollapsed ? 'bg-accent text-white hover:bg-accent/90' : useDarkText ? 'bg-accent text-white hover:bg-accent/90' : 'bg-white/10 text-white border border-white/30 hover:bg-white/20'
              }`}
            >
              Investor Relations
            </Link>
          </nav>

          <Button
            size="icon"
            variant="ghost"
            className={`md:hidden rounded-lg ${isCollapsed ? 'text-slate-600' : useDarkText ? 'text-slate-600' : 'text-white'}`}
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={isMobileMenuOpen}
          >
            {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </Button>
        </motion.div>
      </motion.header>

      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[100] md:hidden"
            aria-modal="true"
          >
            <div
              className="absolute inset-0 bg-slate-900/95 backdrop-blur-sm"
              onClick={() => setIsMobileMenuOpen(false)}
              aria-hidden="true"
            />
            <button
              onClick={() => setIsMobileMenuOpen(false)}
              className="absolute top-6 right-6 z-[120] p-2 rounded-lg text-white hover:bg-white/10"
              aria-label="Close menu"
            >
              <X className="h-8 w-8" />
            </button>
            <motion.nav
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.2 }}
              className="relative z-[110] flex flex-col items-center justify-center min-h-screen gap-6 pt-24 pb-12 px-6"
            >
              {NAV_ITEMS.map((item) =>
                item.href.startsWith('/') ? (
                  <Link
                    key={item.label}
                    to={item.href}
                    className="text-xl font-medium text-white hover:text-accent transition-colors"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {item.label}
                  </Link>
                ) : (
                  <a
                    key={item.label}
                    href={item.href}
                    className="text-xl font-medium text-white hover:text-accent transition-colors"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {item.label}
                  </a>
                )
              )}
              <Link
                to="/investors"
                onClick={() => setIsMobileMenuOpen(false)}
                className="mt-4 px-8 py-4 rounded-lg bg-accent text-white font-semibold hover:bg-accent/90"
              >
                Investor Relations
              </Link>
            </motion.nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}
